import { PageHeader } from '@/components/shared/PageHeader';
import { MetricCard } from '@/components/shared/MetricCard';
import { cn } from '@/lib/utils';
import {
    Users, Camera, CreditCard, Swords, GraduationCap, UserPlus,
    RefreshCw, XCircle, History, Flag, ShoppingBag, Calendar,
    LayoutDashboard, Mail, IdCard, UserCheck, BarChart3, Search,
} from 'lucide-react';
import {
    useAnalyticsDashboard,
    type AnalyticsPeriod,
} from '@/hooks/useAdminAnalytics';
import { useProductPulse } from '@/hooks/useProductPulse';
import { useAdminOpsSnapshot } from '@/hooks/useAdminOpsSnapshot';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const PERIODS: { value: AnalyticsPeriod; label: string }[] = [
    { value: 'day', label: 'Hoy' },
    { value: 'week', label: '7 días' },
    { value: 'month', label: '30 días' },
];

interface AreaTile {
    label: string;
    path: string;
    icon: React.ReactNode;
    description: string;
    badge?: number;
    badgeLabel?: string;
    tone?: 'neutral' | 'warn' | 'ok';
}

export function DashboardPage() {
    const [period, setPeriod] = useState<AnalyticsPeriod>('month');

    const { data: dashboardData, isLoading: dashLoading, refetch } = useAnalyticsDashboard(period);
    // Solo para Onboarding OK en la fila de KPIs (+ alertas → Analytics)
    const { data: productPulse } = useProductPulse(30);
    const ops = useAdminOpsSnapshot();

    const metrics = dashboardData?.metrics;
    const onboardingOk = productPulse?.kpis?.find((k) => k.key === 'onboarding_v2_completed')?.current ?? null;
    const alertCount = productPulse?.alerts?.length ?? 0;

    const areas: AreaTile[] = [
        {
            label: 'Analytics',
            path: '/analytics?tab=landing',
            icon: <BarChart3 size={18} />,
            description: 'Primeras sesiones, perfiles y producto',
            badge: alertCount,
            badgeLabel: alertCount > 0 ? `${alertCount} alertas` : 'Abrir',
            tone: alertCount > 0 ? 'warn' : 'ok',
        },
        {
            label: 'Web',
            path: '/seo',
            icon: <Search size={18} />,
            description: 'SEO, GA4 landing y embudos',
        },
        {
            label: 'Usuarios',
            path: '/users',
            icon: <Users size={18} />,
            description: 'Cuentas y perfiles',
            badge: metrics?.registered_users,
            badgeLabel: metrics ? `${metrics.registered_users.toLocaleString()} regs` : undefined,
        },
        {
            label: 'Reportes',
            path: '/moderation',
            icon: <Flag size={18} />,
            description: 'Moderación de contenido',
            badge: ops.moderationPending,
            badgeLabel: ops.moderationPending > 0 ? `${ops.moderationPending} pend.` : 'Al día',
            tone: ops.moderationPending > 0 ? 'warn' : 'ok',
        },
        {
            label: 'Clubes',
            path: '/clubs',
            icon: <Users size={18} />,
            description: 'Aprobaciones y reportes',
            badge: ops.clubsPending + ops.clubsReportsOpen,
            badgeLabel:
                ops.clubsPending + ops.clubsReportsOpen > 0
                    ? `${ops.clubsPending} pend. · ${ops.clubsReportsOpen} reports`
                    : 'Al día',
            tone: ops.clubsPending + ops.clubsReportsOpen > 0 ? 'warn' : 'ok',
        },
        {
            label: 'Eventos',
            path: '/events',
            icon: <Calendar size={18} />,
            description: 'Eventos pendientes',
            badge: ops.eventsPending,
            badgeLabel: ops.eventsPending > 0 ? `${ops.eventsPending} pend.` : 'Al día',
            tone: ops.eventsPending > 0 ? 'warn' : 'ok',
        },
        {
            label: 'Tienda',
            path: '/store',
            icon: <ShoppingBag size={18} />,
            description: 'Pedidos pendientes de envío',
            badge: ops.storePending,
            badgeLabel: ops.storePending > 0 ? `${ops.storePending} envío` : 'Al día',
            tone: ops.storePending > 0 ? 'warn' : 'ok',
        },
        {
            label: 'Suscripciones',
            path: '/subscriptions',
            icon: <CreditCard size={18} />,
            description: 'Pro, config y cupones',
            badge: metrics?.subscribers_active,
            badgeLabel: metrics
                ? `${metrics.pro_paid_active ?? 0} pago · ${metrics.pro_coupon_active ?? 0} cupón`
                : undefined,
        },
        {
            label: 'Comunicación',
            path: '/communications',
            icon: <Mail size={18} />,
            description: 'Journeys, push y popups',
        },
        {
            label: 'Licencias',
            path: '/licenses',
            icon: <IdCard size={18} />,
            description: 'Licencias, navegación y gestiones',
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader title="Dashboard">
                <button
                    onClick={() => refetch()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                >
                    <RefreshCw size={14} />
                    Actualizar
                </button>
            </PageHeader>

            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit">
                {PERIODS.map((p) => (
                    <button
                        key={p.value}
                        onClick={() => setPeriod(p.value)}
                        className={cn(
                            'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                            period === p.value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {dashLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-[100px] rounded-xl border border-border bg-card animate-pulse" />
                    ))}
                </div>
            ) : metrics ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <MetricCard title="Registrados" value={metrics.registered_users} icon={<UserPlus size={18} />} iconColor="text-indigo-400" />
                    <MetricCard
                        title="Onboarding OK"
                        value={onboardingOk ?? '—'}
                        icon={<UserCheck size={18} />}
                        iconColor="text-teal-400"
                        format={typeof onboardingOk === 'number' ? 'number' : 'none'}
                    />
                    <MetricCard title="Usuarios activos" value={metrics.active_users} icon={<Users size={18} />} iconColor="text-ocean-400" />
                    <MetricCard title="Capturas" value={metrics.total_captures} icon={<Camera size={18} />} iconColor="text-teal-500" />
                    <MetricCard title="Quizzes" value={metrics.quiz_sessions} icon={<GraduationCap size={18} />} iconColor="text-purple-400" />
                    <MetricCard title="Batallas" value={metrics.battles_count} icon={<Swords size={18} />} iconColor="text-rose-400" />
                </div>
            ) : null}

            {metrics && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard title="Pro activos ahora" value={metrics.subscribers_active} icon={<CreditCard size={18} />} iconColor="text-emerald-400" />
                    <MetricCard title="Altas en periodo" value={metrics.subscribers_new_in_period ?? 0} icon={<UserPlus size={18} />} iconColor="text-indigo-400" />
                    <MetricCard title="Bajas en periodo" value={metrics.subscribers_ended_in_period ?? 0} icon={<XCircle size={18} />} iconColor="text-rose-400" />
                    <MetricCard title="Cancelados (hist.)" value={metrics.subscribers_canceled} icon={<History size={18} />} iconColor="text-amber-400" />
                </div>
            )}

            <div className="rounded-xl border border-border bg-card p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-4">
                    <LayoutDashboard size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Apartados del proyecto</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {areas.map((area) => (
                        <Link
                            key={area.path + area.label}
                            to={area.path}
                            className={cn(
                                'rounded-lg border px-3 py-3 flex items-start gap-3 transition-colors',
                                'hover:border-primary/40 hover:bg-muted/30',
                                area.tone === 'warn'
                                    ? 'border-amber-500/30 bg-amber-500/5'
                                    : 'border-border bg-card/50'
                            )}
                        >
                            <span className="mt-0.5 text-muted-foreground">{area.icon}</span>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-semibold text-foreground">{area.label}</p>
                                    {area.badgeLabel && (
                                        <span
                                            className={cn(
                                                'text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0',
                                                area.tone === 'warn'
                                                    ? 'bg-amber-500/15 text-amber-400'
                                                    : area.tone === 'ok'
                                                        ? 'bg-emerald-500/15 text-emerald-400'
                                                        : 'bg-muted text-muted-foreground'
                                            )}
                                        >
                                            {area.badgeLabel}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">{area.description}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
